import type { ReactNode } from 'react';
import { Alert, Card } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  getTelevision,
  type PATVAccount,
  type PATVPurchase,
  type PATelevision,
} from '../api/tv';
import { formatInstant, formatInstantDay } from '../format';
import { DataTable, FactList, type Column } from '../money/DataTable';
import { MoneyPage, QueryFrame } from '../money/MoneyPage';
import { Money } from '../money/Money';
import { usePAQuery } from '../money/usePAQuery';
import { tvPacketLabel, tvStateLabel, tvStateTone } from '../money/labels';

/**
 * ============================================================================
 * «ЦИФРОВОЕ ТВ» — 24TV, READ-ONLY, AND THE READ-ONLINESS IS THE RULE (S23(b))
 * ============================================================================
 *
 * ⚠ THERE IS NO SWITCHING CONTROL ON THIS SCREEN — NOT ENABLED, NOT DISABLED, NOT ANY.
 * Appendix L §3 and F31 describe connect / disconnect / replace-main / «Отмена перехода» for
 * an Account governed by the FULL 24TV integration mode. `answers.md` P6-T7/P6-T8 suspended
 * that mode until after the production launch and the build REFUSES to save an instance in
 * it — so on every 24TV Account this installation has, those controls would be buttons that
 * control nothing: the D109 dead-control class on the one screen where the operator never
 * sees the failure, because the subscriber simply concludes the portal is broken.
 *
 * Questionnaire S23's answer (b) is worded «render the switching controls DISABLED», and this
 * screen renders NONE. The difference is deliberate and it is this portal's own rule, stated
 * in `nav.ts` and enforced by `nav.test.tsx`: a control a subscriber can see and cannot use is
 * a telephone call, and he cannot ask a colleague what a greyed-out button means. What (b)
 * actually asks for is that the packages be SHOWN and that nothing here offer to change them
 * — which is what this does, with the cross-note carrying the rest.
 *
 * `switching_managed_externally` is read as a FACT rather than assumed: the cross-note is
 * rendered from the field, so the day the full mode is un-suspended the field becomes false,
 * the note disappears and the switching controls arrive with it — instead of a comment
 * somebody has to find.
 *
 * # ⚠ `activation_needed` IS THE MONTHLY HOLE AND IT IS THE POINT OF THE WHOLE SECTION
 *
 * The bundle is pushed when 24TV calls `login` — i.e. when the subscriber OPENS THE
 * APPLICATION — and the window is the calendar month. So an ENTITLED subscriber who has not
 * opened it since the month turned has NO TELEVISION until he does, and nothing in this
 * system can do it for him. A blank empty state cannot say that; «где моё ТВ?» is exactly the
 * call this section exists to prevent. The state also covers a claim stranded at «pending»
 * (D251) and one that failed, because opening the application re-arms the push in all three
 * cases — which is why one sentence is correct for all three and why the raw provisioning
 * outcome is deliberately not on the wire.
 *
 * # What is shown, and what cannot be
 *
 * EVERY figure here is LOCAL Postgres: the combo entitlement his tariff carries, THIS
 * month's window, and his own purchase history. His live STANDALONE subscriptions are
 * platform-side only — learning them means a live 24TV call with no rate limiter behind it —
 * so the screen says that packages bought inside the application are managed there, rather
 * than implying that an empty list means he has none.
 *
 * A purchase of **0,00** is a BUNDLED activation and is shown, not hidden: the row exists so
 * the activation stays traceable, and hiding it would make a combo subscriber's history look
 * empty in a month he did use his television.
 */
export function TelevisionScreen() {
  return (
    <MoneyPage titleKey="tv.title" leadKey="tv.lead">
      {(contract) => <TelevisionBody contractID={contract.id} currency={contract.currency} />}
    </MoneyPage>
  );
}

function TelevisionBody({ contractID, currency }: { contractID: number; currency: string }) {
  const { t } = useTranslation();
  const { query, reload } = usePAQuery<PATelevision>(
    () => getTelevision(contractID),
    [contractID],
  );

  return (
    <QueryFrame query={query} reload={reload}>
      {(data) => (
        <>
          {data.items.length === 0 ? (
            <Alert type="info" showIcon message={t('tv.noAccounts')} />
          ) : null}

          {data.items.map((account) => (
            <AccountTV key={account.account_id} account={account} currency={currency} />
          ))}

          {/* THE CROSS-NOTE, from the FIELD. See this file's header. */}
          {data.switching_managed_externally ? (
            <Alert type="info" showIcon message={t('tv.managedExternally')} />
          ) : null}
        </>
      )}
    </QueryFrame>
  );
}

/** One Account's television: the state sentence first, because it is the answer he came for. */
function AccountTV({ account, currency }: { account: PATVAccount; currency: string }) {
  const { t } = useTranslation();
  const tone = tvStateTone(account.state);

  const columns: ReadonlyArray<Column<PATVPurchase>> = [
    {
      key: 'at',
      labelKey: 'tv.columnWhen',
      render: (row) => formatInstant(row.at),
    },
    {
      key: 'packet',
      labelKey: 'tv.columnPacket',
      track: 'minmax(0, 2fr)',
      render: (row) => tvPacketLabel(row.packet, t),
    },
    {
      key: 'amount',
      labelKey: 'tv.columnAmount',
      className: 'pa-td-right',
      // ⚠ ZERO IS A REAL AND MEANINGFUL VALUE HERE — a bundled activation — so it is printed
      // and then EXPLAINED, rather than being blanked or dropped from the list.
      render: (row) => (
        <>
          <Money minor={row.amount} currency={currency} />
          {row.amount === 0 ? <span className="pa-cell-sub">{t('tv.bundled')}</span> : null}
        </>
      ),
    },
  ];

  return (
    <Card
      className="pa-block"
      title={<span className="mono">{account.login}</span>}
      extra={<span className={`pa-status pa-status-${tone}`}>{tvStateLabel(account.state, t)}</span>}
      data-testid={`pa-tv-${account.account_id}`}
    >
      {/* The sentence for the state, and there is one for every value of the closed set —
          including the two that are not about him at all. */}
      <Alert
        type={account.state === 'activation_needed' ? 'warning' : 'info'}
        showIcon
        message={t(`tv.advice.${account.state}`, { defaultValue: t('tv.adviceUnknown') })}
        style={{ marginBottom: 16 }}
      />

      <FactList facts={tvFacts(account, t)} />

      <div className="pa-subblock">
        <h3 className="pa-section-subtitle">{t('tv.purchasesTitle')}</h3>
        <DataTable
          columns={columns}
          rows={account.purchases}
          rowKey={(row) => `${row.at}-${row.packet.packet_id}`}
          emptyKey="tv.noPurchases"
        />
        <p className="pa-field-hint">{t('tv.purchasesNote')}</p>
      </div>
    </Card>
  );
}

/**
 * The facts: what his tariff bundles, this month's window, and whether 24TV has ever seen him.
 *
 * ⚠ THE WINDOW IS LABELLED «текущий месяц» AND NOT «до», because that is what it is: 24TV's
 * own stored series runs to 23:59:59 of the month's last day, and a subscriber told «доступ до
 * 31.08» would read it as a deadline he must renew rather than as the month he is in.
 */
function tvFacts(account: PATVAccount, t: TFunction) {
  const facts: Array<{ key: string; label: string; value: ReactNode; hint?: ReactNode }> = [
    {
      key: 'combo',
      label: t('tv.comboLabel'),
      // «Пакет №N» when the operator has never fetched the catalogue — see `tvPacketLabel`.
      value: account.combo ? tvPacketLabel(account.combo, t) : t('tv.noCombo'),
      hint: account.combo ? undefined : t('tv.noComboHint'),
    },
  ];
  if (account.window) {
    facts.push({
      key: 'window',
      label: t('tv.windowLabel'),
      value: t('tv.windowValue', {
        from: formatInstantDay(account.window.start_at),
        to: formatInstantDay(account.window.end_at),
      }),
    });
  }
  facts.push({
    key: 'linked',
    label: t('tv.linkedLabel'),
    // FALSE IS A NORMAL STATE — it is every subscriber who has not opened the application —
    // so it reads as a fact and not as a fault.
    value: account.linked
      ? t('tv.linkedYes', { when: formatInstant(account.last_seen_at) })
      : t('tv.linkedNo'),
  });
  return facts;
}
